import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  type CreateProjectCommentDto,
  type ProjectCommentDto,
  type ProjectCommentParticipantDto,
  type ProjectCommentTargetKind,
} from '@paladin-monorepo/common';
import { projectsApi } from '@/lib/api/projects';
import { useAuth } from '@/lib/auth';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { formatRelativeTime } from '@/utils/formatRelativeTime';
import { Loader2, MessageSquare, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

function commentQueryKey(projectId: string, targetKind: ProjectCommentTargetKind, targetId: string) {
  return ['project-comments', projectId, targetKind, targetId] as const;
}

function CommentBlock({
  c,
  depth,
  currentUserId,
  canModerate,
  onReply,
  onDelete,
  isDeleting,
  i18nLanguage,
}: {
  c: ProjectCommentDto;
  depth: number;
  currentUserId?: string;
  canModerate: boolean;
  onReply: (parentId: string) => void;
  onDelete: (id: string) => void;
  isDeleting: boolean;
  i18nLanguage: string;
}) {
  const { t } = useTranslation();
  const canDelete = canModerate || (currentUserId != null && c.author.id === currentUserId);
  return (
    <div
      id={`comment-${c.id}`}
      className={`rounded-md border border-border/80 bg-background p-3 ${depth > 0 ? 'ml-4 mt-2 border-l-2 border-l-primary/40' : ''}`}
    >
      <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-muted-foreground">
        <span className="font-medium text-foreground">{c.author.displayName}</span>
        <span>{formatRelativeTime(c.createdAt, i18nLanguage)}</span>
      </div>
      <p className="mt-2 whitespace-pre-wrap text-sm text-foreground">{c.body}</p>
      <div className="mt-2 flex flex-wrap gap-2">
        {depth === 0 && (
          <Button type="button" variant="ghost" size="sm" className="h-7 text-xs" onClick={() => onReply(c.id)}>
            {t('workspace.comments.reply')}
          </Button>
        )}
        {canDelete && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-7 text-xs text-destructive"
            disabled={isDeleting}
            onClick={() => {
              if (window.confirm(t('workspace.comments.confirmDelete'))) onDelete(c.id);
            }}
          >
            <Trash2 className="h-3 w-3 mr-1" />
            {t('workspace.comments.delete')}
          </Button>
        )}
      </div>
      {c.replies.map((r) => (
        <CommentBlock
          key={r.id}
          c={r}
          depth={depth + 1}
          currentUserId={currentUserId}
          canModerate={canModerate}
          onReply={onReply}
          onDelete={onDelete}
          isDeleting={isDeleting}
          i18nLanguage={i18nLanguage}
        />
      ))}
    </div>
  );
}

export function ProjectEntityComments({
  projectId,
  targetKind,
  targetId,
  layout = 'default',
}: {
  projectId: string;
  targetKind: ProjectCommentTargetKind;
  targetId: string;
  /** `dialog`: no section chrome (used inside comment dialog). */
  layout?: 'default' | 'dialog';
}) {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const { user } = useAuth({ middleware: 'auth', tmp: 'ProjectEntityComments' });
  const canModerate =
    user?.systemRole === 'SUPERADMIN' ||
    user?.systemRole === 'SUPPORT' ||
    user?.role === 'SUPERADMIN' ||
    user?.role === 'SUPPORT';
  const qk = commentQueryKey(projectId, targetKind, targetId);

  const [draft, setDraft] = useState('');
  const [replyParentId, setReplyParentId] = useState<string | null>(null);
  const [selectedMentions, setSelectedMentions] = useState<Set<string>>(new Set());

  const { data: comments = [], isLoading } = useQuery({
    queryKey: qk,
    enabled: !!projectId && !!targetId,
    queryFn: () => projectsApi.listComments(projectId, targetKind, targetId),
  });

  const { data: participants = [] } = useQuery({
    queryKey: ['project-comment-participants', projectId],
    enabled: !!projectId,
    queryFn: () => projectsApi.listCommentParticipants(projectId),
  });

  const mentionOptions = useMemo(
    () => participants.filter((p) => p.id !== user?.id),
    [participants, user?.id],
  );

  const createMutation = useMutation({
    mutationFn: (payload: CreateProjectCommentDto) => projectsApi.createComment(projectId, payload),
    onSuccess: () => {
      setDraft('');
      setReplyParentId(null);
      setSelectedMentions(new Set());
      queryClient.invalidateQueries({ queryKey: qk });
      queryClient.invalidateQueries({ queryKey: ['projectActivity', projectId] });
      queryClient.invalidateQueries({ queryKey: ['project-comment-summaries', projectId] });
      queryClient.invalidateQueries({ queryKey: ['project', projectId] });
      toast.success(t('workspace.comments.posted'));
    },
    onError: () => {
      toast.error(t('workspace.comments.postError'));
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (commentId: string) => projectsApi.deleteComment(projectId, commentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: qk });
      queryClient.invalidateQueries({ queryKey: ['projectActivity', projectId] });
      queryClient.invalidateQueries({ queryKey: ['project-comment-summaries', projectId] });
      queryClient.invalidateQueries({ queryKey: ['project', projectId] });
      toast.success(t('workspace.comments.deleted'));
    },
    onError: () => {
      toast.error(t('workspace.comments.deleteError'));
    },
  });

  const toggleMention = (id: string, checked: boolean) => {
    setSelectedMentions((prev) => {
      const next = new Set(prev);
      if (checked) {
        next.add(id);
      } else {
        next.delete(id);
      }
      return next;
    });
  };

  const submit = () => {
    const body = draft.trim();
    if (!body || createMutation.isPending) return;
    const payload: CreateProjectCommentDto = {
      targetKind,
      targetId,
      body,
      parentId: replyParentId ?? undefined,
      mentionedUserIds: selectedMentions.size ? [...selectedMentions] : undefined,
    };
    createMutation.mutate(payload);
  };

  const rootClass =
    layout === 'dialog' ? 'space-y-3' : 'mt-4 space-y-3 border-t border-border pt-3';

  return (
    <div className={rootClass}>
      {layout !== 'dialog' && (
        <div className="flex items-center gap-2 text-sm font-medium text-foreground">
          <MessageSquare className="h-4 w-4" aria-hidden />
          {t('workspace.comments.title')}
        </div>
      )}
      {isLoading ? (
        <div className="flex items-center gap-2 text-muted-foreground text-sm">
          <Loader2 className="h-4 w-4 animate-spin" />
          {t('workspace.comments.loading')}
        </div>
      ) : comments.length === 0 ? (
        <p className="text-sm text-muted-foreground">{t('workspace.comments.empty')}</p>
      ) : (
        <ul className="space-y-3">
          {comments.map((c) => (
            <li key={c.id}>
              <CommentBlock
                c={c}
                depth={0}
                currentUserId={user?.id}
                canModerate={!!canModerate}
                onReply={(id) => {
                  setReplyParentId(id);
                  setDraft('');
                }}
                onDelete={(id) => deleteMutation.mutate(id)}
                isDeleting={deleteMutation.isPending}
                i18nLanguage={i18n.language}
              />
            </li>
          ))}
        </ul>
      )}

      {replyParentId && (
        <p className="text-xs text-muted-foreground">
          {t('workspace.comments.replyingToThread')}{' '}
          <Button type="button" variant="link" className="h-auto p-0 text-xs" onClick={() => setReplyParentId(null)}>
            {t('workspace.comments.cancelReply')}
          </Button>
        </p>
      )}

      <div className="space-y-2">
        <Textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value.slice(0, 8000))}
          placeholder={t('workspace.comments.bodyPlaceholder')}
          rows={3}
          disabled={createMutation.isPending}
        />
        {mentionOptions.length > 0 && (
          <div className="rounded-md border border-border/60 bg-muted/20 p-3 space-y-2">
            <Label className="text-xs font-medium">{t('workspace.comments.mentionsLabel')}</Label>
            <ul className="max-h-32 space-y-2 overflow-y-auto text-sm">
              {mentionOptions.map((p: ProjectCommentParticipantDto) => (
                <li key={p.id} className="flex items-center gap-2">
                  <Checkbox
                    id={`mention-${projectId}-${targetId}-${p.id}`}
                    checked={selectedMentions.has(p.id)}
                    onCheckedChange={(v) => toggleMention(p.id, v === true)}
                    disabled={createMutation.isPending}
                  />
                  <label htmlFor={`mention-${projectId}-${targetId}-${p.id}`} className="cursor-pointer leading-tight">
                    <span className="font-medium">{p.displayName}</span>
                    <span className="text-muted-foreground text-xs ml-1">({p.email})</span>
                  </label>
                </li>
              ))}
            </ul>
          </div>
        )}
        <Button type="button" size="sm" onClick={submit} disabled={!draft.trim() || createMutation.isPending}>
          {createMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : t('workspace.comments.submit')}
        </Button>
      </div>
    </div>
  );
}
